import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Phone, ShieldCheck, Loader2 } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { firebaseConfigured, firebaseSendOtp } from "@/lib/firebase";

export default function EmployeeLogin() {
  const navigate = useNavigate();
  const { user, login, loading: authLoading } = useAuth();
  const [phone, setPhone] = useState("");
  const [code, setCode] = useState("");
  const [step, setStep] = useState("phone");
  const [config, setConfig] = useState(null);
  const [confirmation, setConfirmation] = useState(null);
  const [busy, setBusy] = useState(false);
  const [demoOtp, setDemoOtp] = useState("");

  useEffect(() => {
    api.get("/auth/config")
      .then((r) => setConfig(r.data))
      .catch(() => setConfig({ provider: "demo", demo_enabled: false }));
  }, []);

  useEffect(() => {
    if (!authLoading && user?.role === "employee") navigate("/employee/dashboard");
  }, [user, authLoading, navigate]);

  const sendOtp = async (e) => {
    e.preventDefault();
    if (!/^\d{10}$/.test(phone)) {
      toast.error("Enter a valid 10-digit mobile number");
      return;
    }
    setBusy(true);
    try {
      if (config?.provider === "firebase") {
        if (!firebaseConfigured()) {
          toast.error("Employee login Firebase configuration is missing");
          return;
        }
        const result = await firebaseSendOtp(phone);
        setConfirmation(result);
      } else {
        const { data } = await api.post("/auth/send-otp", { phone });
        if (data.demo_otp) {
          setDemoOtp(String(data.demo_otp));
          toast.info("Development OTP: " + data.demo_otp);
        } else {
          toast.success("OTP requested");
        }
      }
      setStep("otp");
      toast.success("Enter the OTP to continue");
    } catch (err) {
      toast.error(
        typeof err.response?.data?.detail === "string"
          ? err.response.data.detail
          : err.message || "Could not send OTP"
      );
    } finally {
      setBusy(false);
    }
  };

  const verifyOtp = async (e) => {
    e.preventDefault();
    if (!/^\d{6}$/.test(code)) {
      toast.error("Enter the 6-digit OTP");
      return;
    }
    setBusy(true);
    try {
      let data;
      if (config?.provider === "firebase") {
        if (!confirmation) throw new Error("Request an OTP first");
        const credential = await confirmation.confirm(code);
        const idToken = await credential.user.getIdToken();
        ({ data } = await api.post("/employee/auth/firebase-verify", {
          id_token: idToken
        }));
      } else {
        ({ data } = await api.post("/employee/auth/verify-otp", {
          phone,
          code
        }));
      }

      if (data.user?.role !== "employee") {
        throw new Error("This login is not an employee account");
      }
      login(data.token, data.user);
      toast.success("Employee login successful");
      navigate("/employee/dashboard");
    } catch (err) {
      toast.error(
        typeof err.response?.data?.detail === "string"
          ? err.response.data.detail
          : err.message || "OTP verification failed"
      );
    } finally {
      setBusy(false);
    }
  };

  return (
    <main className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
      <section className="w-full max-w-md overflow-hidden rounded-3xl bg-white shadow-xl border border-slate-200">
        <div className="bg-gradient-to-br from-violet-700 to-fuchsia-600 p-8 text-white">
          <div className="mb-4 flex h-12 w-12 items-center justify-center rounded-2xl bg-white/15">
            <img src="/favicon.png" alt="MakeMyEventPro logo" className="h-10 w-10 rounded-xl object-contain bg-white p-1" />
          </div>
          <p className="text-sm font-semibold text-white/80">MakeMyEventPro</p>
          <h1 className="mt-2 text-3xl font-bold">Employee Portal</h1>
          <p className="mt-2 text-sm text-white/85">
            Secure access for authorised employees
          </p>
        </div>
        <div className="p-7">
          {step === "phone" ? (
            <form onSubmit={sendOtp} className="space-y-5">
              <div>
                <label className="mb-2 block text-sm font-medium">Employee mobile number</label>
                <div className="flex items-center rounded-xl border px-3 focus-within:ring-2 focus-within:ring-violet-500">
                  <Phone size={18} className="text-slate-400" />
                  <span className="ml-2 border-r pr-3 text-sm">+91</span>
                  <input
                    className="w-full border-0 p-3 outline-none"
                    value={phone}
                    onChange={(e) => setPhone(e.target.value.replace(/\D/g, "").slice(0, 10))}
                    inputMode="numeric"
                    autoComplete="tel-national"
                    placeholder="10-digit mobile number"
                    required
                  />
                </div>
              </div>
              <div id="recaptcha-container" />
              <button disabled={busy || !config} className="flex w-full items-center justify-center gap-2 rounded-xl bg-violet-700 px-4 py-3 font-semibold text-white disabled:opacity-60">
                {busy && <Loader2 className="animate-spin" size={18} />}
                Send OTP
              </button>
            </form>
          ) : (
            <form onSubmit={verifyOtp} className="space-y-5">
              <p className="text-sm text-slate-600">
                Verify the code for +91 {phone}. Only active employee accounts can sign in.
              </p>
              <div>
                <label className="mb-2 block text-sm font-medium">6-digit OTP</label>
                <input
                  className="w-full rounded-xl border p-3 text-center text-xl tracking-[0.4em]"
                  value={code}
                  onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  maxLength={6}
                  required
                />
              </div>
              {demoOtp && config?.provider !== "firebase" && (
                <p className="text-xs text-amber-700">Development OTP is shown by the server for testing.</p>
              )}
              <button disabled={busy} className="flex w-full items-center justify-center gap-2 rounded-xl bg-violet-700 px-4 py-3 font-semibold text-white disabled:opacity-60">
                {busy && <Loader2 className="animate-spin" size={18} />}
                Verify and Sign In
              </button>
              <button type="button" disabled={busy} onClick={() => { setStep("phone"); setCode(""); setConfirmation(null); setDemoOtp(""); }} className="w-full py-2 text-sm font-medium text-violet-700">
                Change mobile number
              </button>
            </form>
          )}
          <p className="mt-6 text-center text-xs text-slate-500">
            Vendor and customer login remains unchanged.
          </p>
          <Link to="/" className="mt-3 block text-center text-sm text-slate-600 hover:text-violet-700">
            Back to website
          </Link>
        </div>
      </section>
    </main>
  );
}
